/**
 * Tag candidate review queue — clustered proposals to name a type-only
 * ("Category") association as a specific record. One candidate per unknown
 * entity: accepting mints (or links) the record and promotes EVERY member
 * thread's association in place; rejecting is candidate-terminal — that
 * name is never re-proposed for the Category, on any thread.
 */

import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useRequiredTenantId} from '@/matic/hooks/useRequiredTenantId';
import {useToast} from '@/matic/components/ui/toast';
import {getRelayApi} from '../api/relay-api';
import {relayKeys} from '../query-keys';

export function useTagCandidates() {
  const tenantId = useRequiredTenantId();
  const api = getRelayApi();

  return useQuery({
    queryKey: relayKeys.tagCandidates.pending(tenantId),
    queryFn: async () => api.listTagCandidates('pending'),
    staleTime: 60 * 1000,
  });
}

export function useTagCandidateMutations() {
  const tenantId = useRequiredTenantId();
  const api = getRelayApi();
  const queryClient = useQueryClient();
  const {toast} = useToast();

  const invalidate = async (threadIds: string[]) => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: relayKeys.tagCandidates.pending(tenantId),
      }),
      // Accepting re-points N member associations (type-only → record) —
      // refresh every member thread and both feeds.
      ...threadIds.map(async (threadId) =>
        queryClient.invalidateQueries({
          queryKey: relayKeys.threads.detail(threadId, tenantId),
        })
      ),
      queryClient.invalidateQueries({
        queryKey: relayKeys.threads.triage(tenantId),
      }),
      queryClient.invalidateQueries({
        queryKey: relayKeys.threads.all(tenantId),
      }),
    ]);
  };

  const acceptMutation = useMutation({
    mutationFn: async ({
      candidateId,
      name,
      fieldValues,
    }: {
      candidateId: string;
      threadIds: string[];
      name?: string;
      fieldValues?: Record<string, unknown>;
    }) => api.acceptTagCandidate(candidateId, name, fieldValues),
    async onSuccess(result, {threadIds}) {
      // "I need more input" is a 200, and it must be handled BEFORE the
      // promotedCount check below — nothing was promoted precisely because nothing
      // was minted, so falling through would report "this suggestion no longer
      // applies" for a candidate that is still perfectly valid and merely needs
      // field values. The card reads the outcome off the mutation result and
      // expands into a form; there is nothing to toast and nothing to invalidate,
      // since no write happened.
      if (result.outcome === 'needs_fields') return;

      await invalidate(threadIds);
      if (result.promotedCount === 0) {
        toast({
          title: 'No matching threads left to promote',
          description:
            'This suggestion no longer applies — the underlying associations changed.',
        });
        return;
      }

      toast({
        title:
          result.outcome === 'associated_existing'
            ? 'Linked to the existing tag'
            : `Created ${result.candidate.displayName}`,
        variant: 'success',
      });
    },
    onError() {
      toast({title: "Couldn't apply the suggestion", variant: 'error'});
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async ({
      candidateId,
    }: {
      candidateId: string;
      threadIds: string[];
    }) => api.rejectTagCandidate(candidateId),
    async onSuccess(_result, {threadIds}) {
      await invalidate(threadIds);
    },
    onError() {
      toast({title: "Couldn't dismiss the suggestion", variant: 'error'});
    },
  });

  return {acceptMutation, rejectMutation};
}
